import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  AudioWaveform,
  BarChart3,
  BookOpen,
  Crown,
  Download,
  FileAudio,
  Headphones,
  Home,
  Import,
  Library,
  ListMusic,
  LogOut,
  Menu,
  Mic,
  Music2,
  SlidersHorizontal,
  Sparkles,
  ShieldCheck,
  User,
  X,
  Youtube,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/lib/auth";

const primaryNav = [
  { label: "Dashboard", to: "/dashboard", icon: Home, exact: true },
  { label: "Analysis", to: "/dashboard", icon: BarChart3, match: ["/analysis"] },
  { label: "Practice / Mixer", to: "/dashboard", icon: Headphones, match: ["/stem-mixer"] },
  { label: "YouTube Practice", to: "/youtube-practice", icon: Youtube },
  { label: "Media Import", to: "/media-import", icon: Import },
  { label: "Tono y BPM", to: "/pitch-tempo", icon: SlidersHorizontal },
  { label: "Download Songs", to: "/download-songs", icon: Download },
  { label: "Library", to: "/library", icon: Library },
  { label: "Setlists", to: "/setlists", icon: ListMusic },
  { label: "Recordings", to: "/record", icon: Mic },
  { label: "Assistant", to: "/assistant", icon: Sparkles },
  { label: "Tutorials", to: "/tutorials", icon: BookOpen },
  { label: "Pricing / Pro", to: "/pricing", icon: Crown },
];

const bottomNav = [
  { label: "Home", to: "/dashboard", icon: Home },
  { label: "Practice", to: "/youtube-practice", icon: Youtube },
  { label: "Import", to: "/media-import", icon: Import },
  { label: "Record", to: "/record", icon: Mic },
  { label: "Pro", to: "/pricing", icon: Crown },
];

const adminNavItem = { label: "Music Catalog", to: "/admin/music-catalog", icon: ShieldCheck, match: ["/admin"] };

const isActiveItem = (pathname, item) => {
  if (item.exact) return pathname === item.to;
  if (pathname === item.to || pathname.startsWith(`${item.to}/`)) return true;
  return item.match?.some((prefix) => pathname.startsWith(prefix));
};

const NavItem = ({ item, collapsed = false, onNavigate }) => {
  const location = useLocation();
  const active = isActiveItem(location.pathname, item);
  const Icon = item.icon;

  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      aria-label={item.label}
      className={`group relative flex min-h-11 items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/70 ${
        active
          ? "bg-panel text-foreground"
          : "text-muted-foreground hover:bg-panel/60 hover:text-foreground"
      } ${collapsed ? "justify-center" : ""}`}
    >
      {active && (
        /* channel-strip LED — the active indicator is a lit bar, not a gradient fill */
        <span
          className="absolute -left-3.5 top-1/2 h-[18px] w-[3px] -translate-y-1/2 rounded-full bg-signal shadow-[0_0_10px_rgba(45,216,236,0.75)]"
          aria-hidden="true"
        />
      )}
      <span className={`grid h-9 w-9 place-items-center rounded-xl transition-colors ${active ? "bg-signal/18 text-signal" : "bg-panel text-muted-foreground group-hover:text-signal"}`}>
        <Icon className="h-5 w-5" />
      </span>
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
};

const SidebarContent = ({ collapsed = false, onNavigate }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isAdminUser(user);
  const navigationItems = useMemo(
    () => {
      console.info("[AppShell] admin nav condition", {
        email: user?.email,
        role: user?.role,
        is_admin: user?.is_admin,
        allowed: isAdmin,
      });
      return isAdmin ? [...primaryNav, adminNavItem] : primaryNav;
    },
    [isAdmin, user?.email, user?.is_admin, user?.role]
  );

  const handleLogout = () => {
    logout();
    navigate("/");
    onNavigate?.();
  };

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <Link
        to="/dashboard"
        onClick={onNavigate}
        className={`flex items-center gap-3 rounded-3xl px-2 py-2 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/70 ${collapsed ? "justify-center" : ""}`}
        aria-label="SteelMora Dashboard"
      >
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-signal to-tune shadow-[0_0_34px_rgba(45,216,236,0.24)]">
          <AudioWaveform className="h-6 w-6 text-ink" />
        </span>
        {!collapsed && (
          <span>
            <span className="block font-display text-lg font-black tracking-tight">SteelMora</span>
            <span className="block text-xs font-medium uppercase tracking-[0.28em] text-signal/70">Lab Studio</span>
          </span>
        )}
      </Link>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1 steel-scroll-soft">
        <nav className="space-y-1.5" aria-label="Main navigation">
          {navigationItems.map((item) => (
            <NavItem key={`${item.label}-${item.to}`} item={item} collapsed={collapsed} onNavigate={onNavigate} />
          ))}
        </nav>
      </div>

      <div className="rounded-3xl border border-border bg-panel/60 p-3 shadow-2xl shadow-black/20">
        <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : ""}`}>
          <span className="grid h-10 w-10 place-items-center rounded-2xl bg-signal/15 text-signal">
            <User className="h-5 w-5" />
          </span>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">{user?.name || "Musician"}</p>
              <p className="text-xs capitalize text-muted-foreground">{user?.plan || "free"} plan</p>
            </div>
          )}
        </div>
        {!collapsed && (
          <Button
            type="button"
            variant="ghost"
            onClick={handleLogout}
            className="mt-3 min-h-11 w-full justify-start rounded-2xl text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Cerrar sesion
          </Button>
        )}
      </div>
    </div>
  );
};

const MobileBottomNav = () => {
  const location = useLocation();

  return (
    <nav className="fixed inset-x-2 z-40 grid grid-cols-5 rounded-[1.6rem] border border-border bg-ink-raised/90 p-1 shadow-[0_18px_50px_rgba(0,0,0,0.45)] backdrop-blur-2xl md:hidden steel-bottom-nav" aria-label="Mobile bottom navigation">
      {bottomNav.map((item) => {
        const active = isActiveItem(location.pathname, item);
        const Icon = item.icon;
        return (
          <NavLink
            key={`${item.label}-${item.to}`}
            to={item.to}
            aria-label={item.label}
            className={`flex min-h-12 flex-col items-center justify-center rounded-3xl text-[10px] font-semibold transition-all min-[380px]:text-[11px] ${active ? "bg-signal/18 text-signal" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Icon className="h-4 w-4" />
            <span className="mt-0.5 truncate">{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
};

const MobileTopBar = ({ onMenu }) => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const navigationItems = useMemo(
    () => isAdmin ? [...primaryNav, adminNavItem] : primaryNav,
    [isAdmin]
  );
  const current = useMemo(
    () => navigationItems.find((item) => isActiveItem(location.pathname, item)) || navigationItems[0],
    [location.pathname, navigationItems]
  );

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-border bg-ink-raised/90 px-3 py-2 backdrop-blur-2xl md:hidden">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onMenu}
          aria-label="Open navigation menu"
          className="grid min-h-11 min-w-11 place-items-center rounded-2xl border border-border bg-panel text-foreground shadow-lg shadow-black/20"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1 text-center">
          <p className="truncate font-display text-sm font-black tracking-tight text-foreground">{current.label}</p>
          <p className="text-[10px] uppercase tracking-[0.24em] text-signal/60">SteelMora</p>
        </div>
        <Link
          to="/dashboard"
          aria-label="Go to dashboard"
          className="grid min-h-11 min-w-11 place-items-center rounded-2xl bg-gradient-to-br from-signal to-tune text-ink shadow-[0_8px_20px_rgba(45,216,236,0.18)]"
        >
          <Music2 className="h-5 w-5" />
        </Link>
      </div>
    </header>
  );
};

const AppShell = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <div className="steel-app-shell min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top_left,rgba(45,216,236,0.14),transparent_32rem),radial-gradient(circle_at_80%_0%,rgba(240,166,61,0.09),transparent_28rem),#06080c] text-foreground">
      <div className="pointer-events-none fixed inset-0 z-0 opacity-70 [background-image:linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] [background-size:44px_44px]" />

      <aside className="fixed inset-y-0 left-0 z-30 hidden border-r border-border bg-ink-raised/70 shadow-[20px_0_80px_rgba(0,0,0,0.35)] backdrop-blur-2xl md:block md:w-20 lg:w-72">
        <div className="hidden h-full md:block lg:hidden">
          <SidebarContent collapsed />
        </div>
        <div className="hidden h-full lg:block">
          <SidebarContent />
        </div>
      </aside>

      <MobileTopBar onMenu={() => setMobileOpen(true)} />

      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Navigation menu">
          <button
            type="button"
            aria-label="Close navigation overlay"
            className="absolute inset-0 bg-black/68 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 flex w-[min(88vw,22rem)] flex-col border-r border-border bg-ink-raised/95 shadow-[20px_0_80px_rgba(0,0,0,0.55)] backdrop-blur-2xl">
            <button
              type="button"
              aria-label="Close navigation menu"
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-3 grid min-h-11 min-w-11 place-items-center rounded-2xl border border-border bg-panel text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <main className="relative z-10 min-h-screen min-w-0 pt-[4.35rem] md:pb-8 md:pl-20 md:pt-0 lg:pl-72 steel-mobile-main">
        <div className="mx-auto min-w-0 max-w-[1540px] px-3 py-4 sm:px-5 md:px-6 lg:px-8 lg:py-8">
          {children}
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default AppShell;
